import { useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import PageHero from '../../components/shared/PageHero';

type Position = {
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
};

const values = [
  {
    icon: 'ri-shield-check-line',
    title: 'Safety First',
    description: 'A zero-harm culture backed by rigorous HSSE training and standards on every site.',
  },
  {
    icon: 'ri-rocket-line',
    title: 'Career Growth',
    description: 'Structured development, OEM certifications, and clear pathways for advancement.',
  },
  {
    icon: 'ri-tools-line',
    title: 'World-class Equipment',
    description: 'Hands-on experience with certified rigs and technology from global OEM partners.',
  },
  {
    icon: 'ri-team-line',
    title: 'Collaborative Teams',
    description: 'Work alongside experienced engineers, technicians, and commercial specialists.',
  },
];

const positions: Position[] = [
  {
    title: 'Drilling Engineer',
    department: 'Operations',
    location: 'Port Harcourt, Nigeria',
    type: 'Full-time',
    description: 'Plan and supervise drilling programmes, rig selection, and well delivery for upstream clients.',
  },
  {
    title: 'Solids Control Technician',
    department: 'Field Services',
    location: 'Warri, Nigeria',
    type: 'Full-time',
    description: 'Install, operate, and maintain solids control and drilling waste management systems on rig sites.',
  },
  {
    title: 'HSSE Officer',
    department: 'HSSE',
    location: 'Port Harcourt, Nigeria',
    type: 'Full-time',
    description: 'Drive HSSE compliance, audits, and training across operations and subcontractor teams.',
  },
  {
    title: 'Business Development Manager',
    department: 'Commercial',
    location: 'Lagos, Nigeria',
    type: 'Full-time',
    description: 'Grow relationships with operators across Nigeria and West Africa and structure equipment financing deals.',
  },
  {
    title: 'Mechanical Maintenance Engineer',
    department: 'Engineering & Support',
    location: 'Port Harcourt, Nigeria',
    type: 'Full-time',
    description: 'Lead preventive maintenance and overhaul of rig equipment to maximise uptime and reliability.',
  },
  {
    title: 'Graduate Engineer Trainee',
    department: 'Engineering',
    location: 'Lagos, Nigeria',
    type: 'Graduate Programme',
    description: 'A 12-month rotational programme across operations, engineering, and HSSE for top engineering graduates.',
  },
];

const benefits = [
  { icon: 'ri-heart-pulse-line', title: 'Health Cover', description: 'Comprehensive HMO for you and your family.' },
  { icon: 'ri-money-dollar-circle-line', title: 'Competitive Pay', description: 'Market-leading salaries, allowances, and performance bonuses.' },
  { icon: 'ri-book-open-line', title: 'Learning Budget', description: 'Funded certifications, IWCF/IADC training, and OEM courses.' },
  { icon: 'ri-safe-2-line', title: 'Pension', description: 'Contributory pension scheme in line with PenCom regulations.' },
  { icon: 'ri-calendar-check-line', title: 'Paid Leave', description: 'Generous annual leave and rotation schedules for field staff.' },
  { icon: 'ri-global-line', title: 'International Exposure', description: 'Training and factory visits with our global OEM partners.' },
];

const emptyForm = {
  fullName: '',
  email: '',
  phone: '',
  position: '',
  experience: '',
  resume: '',
  coverLetter: '',
};

const inputClass =
  'w-full px-4 py-3 border border-[#D9DEE3] rounded-md text-sm text-[#0F172A] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#0875D4] focus:border-transparent transition';

export default function Careers() {
  const [formData, setFormData] = useState(emptyForm);
  const [department, setDepartment] = useState('All');
  const [submitted, setSubmitted] = useState(false);

  const departments = ['All', ...Array.from(new Set(positions.map((p) => p.department)))];
  const filtered = department === 'All' ? positions : positions.filter((p) => p.department === department);

  const update = (field: keyof typeof emptyForm) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => setFormData({ ...formData, [field]: e.target.value });

  const applyFor = (title: string) => {
    setFormData((prev) => ({ ...prev, position: title }));
    setSubmitted(false);
    document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: connect to the real submission endpoint.
    setSubmitted(true);
    setFormData(emptyForm);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <PageHero
        title="Careers"
        subtitle="Build your career with an indigenous Nigerian company powering the upstream sector."
        image="https://res.cloudinary.com/dpkn1ppzj/image/upload/v1770027201/5876d9f779d5215a8b5363d48db7448b3697b9ed_x7m5zk.png"
      />

      {/* Intro */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-20">
          <h2 className="text-2xl lg:text-[36px] font-medium text-[#333333] mb-6">Why work with us</h2>
          <p className="text-lg text-[#677078] leading-relaxed max-w-4xl">
            At Alphaden Exploration &amp; Production we help operators drill faster, safer, and at lower cost. That
            takes talented people — engineers, technicians, HSSE professionals, and commercial experts — who want to
            make a real impact on Nigeria's energy future.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="pb-20 lg:pb-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v) => (
            <div
              key={v.title}
              className="group bg-white p-8 rounded-xl transition-all duration-300 hover:-translate-y-1"
              style={{ boxShadow: '0px 10px 15px -3px #00000014, 0px 4px 6px -4px #00000014' }}
            >
              <div className="w-[56px] h-[56px] bg-[#E4F1FF] rounded-full flex items-center justify-center mb-6 transition-colors duration-300 group-hover:bg-[#00458F]">
                <i className={`${v.icon} text-2xl text-[#00458F] transition-colors duration-300 group-hover:text-white`}></i>
              </div>
              <h3 className="text-xl font-medium text-[#0F172A] mb-3">{v.title}</h3>
              <p className="text-[#4B5563] leading-relaxed text-[15px]">{v.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Open positions */}
      <section className="py-20 lg:py-28 bg-[#F8FAFC]" id="positions">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10">
            <span className="text-[#E37D33] font-medium text-lg">Join the team</span>
            <h2 className="text-3xl lg:text-[43px] font-medium text-[#010305] mt-3 mb-3">Open positions</h2>
            <p className="text-lg text-[#677078] max-w-2xl mx-auto">
              Explore current opportunities across our operations.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-2 mb-10">
            {departments.map((d) => (
              <button
                key={d}
                id={`filter-${d.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                onClick={() => setDepartment(d)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-300 cursor-pointer ${
                  department === d
                    ? 'bg-[#00458F] text-white'
                    : 'bg-white text-[#4B5563] border border-[#E5EAF0] hover:border-[#0875D4] hover:text-[#0875D4]'
                }`}
              >
                {d}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {filtered.map((p) => (
              <div
                key={p.title}
                className="group bg-white rounded-xl border border-[#E5EAF0] p-6 lg:p-8 flex flex-col lg:flex-row lg:items-center gap-6 hover:border-[#0875D4] hover:shadow-lg transition-all duration-300"
              >
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-xs font-medium uppercase tracking-wide text-[#0875D4] bg-[#E4F1FF] px-3 py-1 rounded-full">
                      {p.department}
                    </span>
                    <span className="text-xs font-medium text-[#677078] bg-[#F1F5F9] px-3 py-1 rounded-full">{p.type}</span>
                  </div>
                  <h3 className="text-xl font-medium text-[#0F172A] mb-2">{p.title}</h3>
                  <p className="text-[#4B5563] leading-relaxed text-[15px] mb-3">{p.description}</p>
                  <p className="text-sm text-[#677078] flex items-center gap-1">
                    <i className="ri-map-pin-line"></i> {p.location}
                  </p>
                </div>
                <button
                  onClick={() => applyFor(p.title)}
                  className="self-start lg:self-center inline-flex items-center gap-2 bg-[#0875D4] hover:bg-[#0663B5] text-white font-medium px-6 py-3 rounded-md transition-colors duration-300 whitespace-nowrap cursor-pointer"
                >
                  Apply now <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1"></i>
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="grid lg:grid-cols-[1fr_1.4fr]">
        <div className="bg-[#00458F] text-white p-12 lg:p-20 flex items-center">
          <div className="max-w-[420px]">
            <h2 className="text-3xl lg:text-[40px] font-medium mb-4">Benefits &amp; perks</h2>
            <p className="leading-relaxed text-white/85">
              A comprehensive package that supports your professional growth and personal wellbeing.
            </p>
          </div>
        </div>
        <div className="bg-[#333333] text-white p-12 lg:p-16 grid sm:grid-cols-2 gap-8">
          {benefits.map((b) => (
            <div key={b.title} className="flex gap-4">
              <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                <i className={`${b.icon} text-xl text-white`}></i>
              </div>
              <div>
                <h3 className="font-medium mb-1">{b.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{b.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section className="py-20 lg:py-28 bg-white" id="apply">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-[43px] font-medium text-[#010305] mb-3">Apply now</h2>
            <p className="text-lg text-[#677078]">
              Don't see the right role? Send a general application and we'll keep you in mind.
            </p>
          </div>

          {submitted && (
            <div className="mb-6 rounded-md border border-[#B7E4C7] bg-[#ECFDF3] text-[#166534] px-4 py-3 text-sm flex items-center gap-2">
              <i className="ri-checkbox-circle-line text-lg"></i>
              Thank you for applying! Our recruitment team will review your submission and contact you soon.
            </div>
          )}

          <form
            onSubmit={handleSubmit}
            className="bg-white p-8 lg:p-10 rounded-xl border border-[#E5EAF0]"
            style={{ boxShadow: '0px 10px 15px -3px #00000014, 0px 4px 6px -4px #00000014' }}
          >
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label htmlFor="career-name" className="block text-sm font-medium text-[#0F172A] mb-2">Full name *</label>
                <input id="career-name" type="text" required value={formData.fullName} onChange={update('fullName')} className={inputClass} placeholder="John Doe" />
              </div>
              <div>
                <label htmlFor="career-email" className="block text-sm font-medium text-[#0F172A] mb-2">Email address *</label>
                <input id="career-email" type="email" required value={formData.email} onChange={update('email')} className={inputClass} placeholder="john@example.com" />
              </div>
              <div>
                <label htmlFor="career-phone" className="block text-sm font-medium text-[#0F172A] mb-2">Phone number *</label>
                <input id="career-phone" type="tel" required value={formData.phone} onChange={update('phone')} className={inputClass} placeholder="+234 800 000 0000" />
              </div>
              <div>
                <label htmlFor="career-position" className="block text-sm font-medium text-[#0F172A] mb-2">Position *</label>
                <select id="career-position" required value={formData.position} onChange={update('position')} className={inputClass}>
                  <option value="">Select a position</option>
                  {positions.map((p) => (
                    <option key={p.title} value={p.title}>{p.title}</option>
                  ))}
                  <option value="General Application">General Application</option>
                </select>
              </div>
              <div>
                <label htmlFor="career-experience" className="block text-sm font-medium text-[#0F172A] mb-2">Years of experience *</label>
                <input id="career-experience" type="text" required value={formData.experience} onChange={update('experience')} className={inputClass} placeholder="e.g. 5 years" />
              </div>
              <div>
                <label htmlFor="career-resume" className="block text-sm font-medium text-[#0F172A] mb-2">CV link *</label>
                <input id="career-resume" type="url" required value={formData.resume} onChange={update('resume')} className={inputClass} placeholder="https://drive.google.com/..." />
              </div>
            </div>

            <div className="mb-8">
              <label htmlFor="career-cover" className="block text-sm font-medium text-[#0F172A] mb-2">Cover letter</label>
              <textarea
                id="career-cover"
                rows={5}
                maxLength={500}
                value={formData.coverLetter}
                onChange={update('coverLetter')}
                className={`${inputClass} resize-none`}
                placeholder="Tell us why you'd like to join Alphaden..."
              ></textarea>
              <p className="text-xs text-[#9CA3AF] mt-2">{formData.coverLetter.length}/500 characters</p>
            </div>

            <button
              id="career-submit"
              type="submit"
              className="w-full bg-[#0875D4] hover:bg-[#0663B5] text-white font-medium px-8 py-4 rounded-md transition-colors duration-300 cursor-pointer"
            >
              Submit application
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}